/**
 * ALOHA PLATFORM
 * Copyright © 2025 Malcolm KH Lee III. All Rights Reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL
 * Unauthorized copying or distribution is strictly prohibited.
 */

import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Link, Navigate } from 'react-router-dom';
import axios from 'axios';
import { Crown, MessageCircle, TrendingUp, Zap, Clock } from 'lucide-react';
import RealisticGalaxyBackground from '../components/RealisticGalaxyBackground';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Dashboard = () => {
  const { user, isAuthenticated, token } = useAuth();
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isAuthenticated) {
      fetchDashboardData();
    }
  }, [isAuthenticated]);

  const fetchDashboardData = async () => {
    try {
      const [subRes, sessionsRes] = await Promise.all([
        axios.get(`${API}/subscriptions/my-plan`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/avatar/sessions`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setSubscriptionData(subRes.data);
      setSessions(sessionsRes.data.sessions || []);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 flex items-center justify-center">
        <div className="text-white text-2xl">Loading...</div>
      </div>
    );
  }

  const tierInfo = subscriptionData?.tier_info || {};
  const usage = subscriptionData?.usage || {};
  const hasAvatarAccess = tierInfo.features?.avatarChat;

  return (
    <div className="min-h-screen relative">
      <RealisticGalaxyBackground />
      <div className="relative z-10 container mx-auto px-6 py-12">
        {/* Header */}
        <div className="mb-12">
          <h1 className="text-5xl font-bold text-white mb-2" data-testid="dashboard-title">
            Welcome, {user.full_name}!
          </h1>
          <p className="text-gray-300 text-xl">Here's your Aloha Platform overview</p>
        </div>

        {/* Admin Control - Malcolm KH Lee III Only */}
        {user.email === 'malcolmlee3@gmail.com' && (
          <div className="bg-gradient-to-r from-yellow-600/80 to-orange-600/80 backdrop-blur-xl border-2 border-yellow-400 shadow-2xl rounded-2xl p-6 mb-8" data-testid="admin-panel">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Crown className="w-10 h-10 text-yellow-300" />
                <div>
                  <h2 className="text-2xl font-bold text-white">Admin Control Panel</h2>
                  <p className="text-yellow-200 text-sm">Full platform access</p>
                </div>
              </div>
              <a
                href="/admin"
                target="_blank"
                rel="noopener noreferrer"
                className="px-6 py-3 bg-white text-orange-600 font-bold rounded-lg hover:bg-yellow-100 transition transform hover:scale-105"
                data-testid="admin-panel-link"
              >
                Open Admin Panel →
              </a>
            </div>
          </div>
        )}

        {/* Subscription Card */}
        <div className="bg-gradient-to-r from-purple-900/80 to-blue-900/80 backdrop-blur-xl border border-white/20 shadow-2xl rounded-2xl p-8 mb-8" data-testid="subscription-card">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <Crown className="w-8 h-8 text-yellow-400" />
                <h2 className="text-3xl font-bold text-white">{tierInfo.name} Plan</h2>
              </div>
              <p className="text-gray-300 text-lg mb-4">
                {hasAvatarAccess ? (
                  <span className="text-green-400 font-semibold">✓ 3D Avatar Chat Enabled</span>
                ) : (
                  <span className="text-yellow-400">Upgrade to unlock 3D Avatar Chat</span>
                )}
              </p>
            </div>
            <Link
              to="/pricing"
              className="px-6 py-3 bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-white font-semibold rounded-lg transition transform hover:scale-105"
              data-testid="upgrade-button"
            >
              {hasAvatarAccess ? 'Manage Plan' : 'Upgrade Now'}
            </Link>
          </div>
        </div>

        {/* Usage Stats */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          <div className="bg-black/40 backdrop-blur-xl border border-white/10 shadow-2xl rounded-xl p-6" data-testid="stat-messages">
            <div className="flex items-center gap-3 mb-3">
              <MessageCircle className="w-6 h-6 text-blue-400" />
              <h3 className="text-white font-semibold">Chat Messages Today</h3>
            </div>
            <p className="text-4xl font-bold text-white">{usage.chat_messages_today || 0}</p>
            <p className="text-gray-400 text-sm mt-2">
              Limit: {tierInfo.limits?.chatMessagesPerDay === -1 ? 'Unlimited' : tierInfo.limits?.chatMessagesPerDay}
            </p>
          </div>

          <div className="bg-black/40 backdrop-blur-xl border border-white/10 shadow-2xl rounded-xl p-6" data-testid="stat-sessions">
            <div className="flex items-center gap-3 mb-3">
              <TrendingUp className="w-6 h-6 text-purple-400" />
              <h3 className="text-white font-semibold">Avatar Sessions</h3>
            </div>
            <p className="text-4xl font-bold text-white">{usage.avatar_sessions_month || 0}</p>
            <p className="text-gray-400 text-sm mt-2">
              Limit: {tierInfo.limits?.avatarSessions === -1 ? 'Unlimited' : tierInfo.limits?.avatarSessions || 0}
            </p>
          </div>

          <div className="bg-black/40 backdrop-blur-xl border border-white/10 shadow-2xl rounded-xl p-6" data-testid="stat-minutes">
            <div className="flex items-center gap-3 mb-3">
              <Clock className="w-6 h-6 text-yellow-400" />
              <h3 className="text-white font-semibold">Avatar Minutes</h3>
            </div>
            <p className="text-4xl font-bold text-white">{Math.round(usage.avatar_minutes_month || 0)}</p>
            <p className="text-gray-400 text-sm mt-2">
              Limit: {tierInfo.limits?.avatarSessionMinutes === -1 ? 'Unlimited' : tierInfo.limits?.avatarSessionMinutes || 0}
            </p>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-black/40 backdrop-blur-xl border border-white/10 shadow-2xl rounded-2xl p-8 mb-8">
          <h2 className="text-2xl font-bold text-white mb-6">Quick Actions</h2>
          <div className="grid md:grid-cols-2 gap-4">
            <Link
              to="/avatar-chat"
              className="flex items-center gap-4 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 rounded-xl p-6 transition transform hover:scale-105"
              data-testid="start-avatar-chat-button"
            >
              <MessageCircle className="w-8 h-8 text-white" />
              <div>
                <h3 className="text-white font-bold text-xl">Start Avatar Chat</h3>
                <p className="text-gray-200 text-sm">
                  {hasAvatarAccess ? 'Begin a new conversation' : 'Requires Pro or Enterprise'}
                </p>
              </div>
            </Link>

            <Link
              to="/pricing"
              className="flex items-center gap-4 bg-gradient-to-r from-yellow-600 to-orange-600 hover:from-yellow-700 hover:to-orange-700 rounded-xl p-6 transition transform hover:scale-105"
              data-testid="view-plans-button"
            >
              <Zap className="w-8 h-8 text-white" />
              <div>
                <h3 className="text-white font-bold text-xl">Explore Plans</h3>
                <p className="text-gray-200 text-sm">Upgrade for more features</p>
              </div>
            </Link>
          </div>
        </div>

        {/* Recent Sessions */}
        {sessions.length > 0 && (
          <div className="bg-black/40 backdrop-blur-xl border border-white/10 shadow-2xl rounded-2xl p-8">
            <h2 className="text-2xl font-bold text-white mb-6">Recent Avatar Sessions</h2>
            <div className="space-y-4">
              {sessions.map((session) => (
                <div
                  key={session.session_id}
                  className="bg-white/5 rounded-lg p-4 flex items-center justify-between"
                  data-testid={`session-${session.session_id}`}
                >
                  <div>
                    <p className="text-white font-semibold">{session.session_id}</p>
                    <p className="text-gray-400 text-sm">
                      {new Date(session.start_time).toLocaleString()}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-white">{session.messages_count || 0} messages</p>
                    <p className="text-gray-400 text-sm">
                      {session.duration_minutes ? `${Math.round(session.duration_minutes)} min` : 'In progress'}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
